import { formatDashboardDateMedium } from "./dashboardTime";

/** Normalize GET /va/settings for distributor dashboard settings. */
export function normalizeVaSettingsResponse(response) {
  const payload =
    response?.item ??
    response?.data ??
    (response && typeof response === "object" ? response : {});

  const profile = payload.profile ?? {
    id: payload.id,
    name: payload.name,
    email: payload.email,
  };

  const rawPayment =
    (payload.paymentMethod && typeof payload.paymentMethod === "object"
      ? payload.paymentMethod
      : null) ||
    (payload.payment && typeof payload.payment === "object"
      ? payload.payment
      : null) ||
    {};

  const accountNumber = String(
    rawPayment.accountNumber ??
      rawPayment.cardNumber ??
      rawPayment.account_number ??
      payload.accountNumber ??
      payload.cardNumber ??
      ""
  ).trim();
  const routingNumber = String(
    rawPayment.routingNumber ??
      rawPayment.routing_number ??
      payload.routingNumber ??
      ""
  ).trim();

  const last4 =
    rawPayment.last4 ??
    rawPayment.accountLast4 ??
    (accountNumber ? accountNumber.slice(-4) : null);
  const routingLast4 =
    rawPayment.routingLast4 ??
    (routingNumber ? routingNumber.slice(-4) : null);

  const isComplete =
    rawPayment.isComplete === true ||
    rawPayment.payoutSetupComplete === true ||
    Boolean(rawPayment.stripeConnectAccountId) ||
    Boolean(last4 && String(last4).length > 0) ||
    Boolean(accountNumber);

  const fromPartsFirst = String(rawPayment.accountHolderFirstName || "").trim();
  const fromPartsLast = String(rawPayment.accountHolderLastName || "").trim();
  const fullHolderName = String(
    rawPayment.accountHolderName ??
      rawPayment.account_holder_name ??
      ""
  ).trim();

  const accountHolderName =
    fullHolderName ||
    [fromPartsFirst, fromPartsLast].filter(Boolean).join(" ") ||
    String(profile?.name ?? payload.name ?? "").trim();

  const nameParts = accountHolderName.split(/\s+/).filter(Boolean);
  const accountHolderFirstName = fromPartsFirst || nameParts[0] || "";
  const accountHolderLastName =
    fromPartsLast || (nameParts.length > 1 ? nameParts.slice(1).join(" ") : "");

  return {
    success: true,
    data: {
      profile: {
        id: profile?.id ?? null,
        name: profile?.name ?? payload.name ?? "",
        email: profile?.email ?? payload.email ?? "",
      },
      paymentMethod: {
        last4: last4 ? String(last4) : null,
        routingLast4: routingLast4 ? String(routingLast4) : null,
        isComplete,
        payoutSetupComplete: Boolean(rawPayment.payoutSetupComplete),
        stripeConnectAccountId: rawPayment.stripeConnectAccountId || null,
        accountHolderName,
        accountHolderFirstName,
        accountHolderLastName,
        accountType: rawPayment.accountType || "checking",
        // Keep for edit popup when API returns full values
        accountNumber,
        routingNumber,
      },
    },
  };
}

/** Masked bank display: ****{last4}. Never show full account after save. */
export function formatMaskedBankAccount(last4) {
  if (!last4) return null;
  return `****${String(last4).slice(-4)}`;
}

const BILL_STATUS_MAP = {
  all: "all",
  paid: "paid",
  pending: "pending",
  failed: "failed",
  cancelled: "cancelled",
  canceled: "cancelled",
};

export function toBillStatusParam(tab) {
  const key = String(tab || "all").toLowerCase();
  return BILL_STATUS_MAP[key] || "all";
}

const BILL_TYPE_MAP = {
  all: "all",
  "order revenue": "order_revenue",
  order_revenue: "order_revenue",
  commission: "commission_payout",
  "commission payout": "commission_payout",
  commission_payout: "commission_payout",
  "return deduction": "return_deduction",
  return_deduction: "return_deduction",
  "order cancelled": "order_cancelled",
  order_cancelled: "order_cancelled",
};

export function toBillTypeParam(tab) {
  const key = String(tab || "all").trim().toLowerCase();
  return BILL_TYPE_MAP[key] || "all";
}

export function formatBillTypeLabel(type) {
  const raw = String(type || "").toLowerCase();
  if (raw === "order_revenue") return "Order Revenue";
  if (raw === "commission_payout") return "Commission Payout";
  if (raw === "return_deduction") return "Return Deduction";
  if (raw === "order_cancelled") return "Order Cancelled";
  return type ? String(type) : "—";
}

function formatBillDate(value) {
  if (!value) return "—";
  if (typeof value === "string" && !/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value;
  }
  // Always Pacific — DB timestamps stay UTC.
  const formatted = formatDashboardDateMedium(value, "");
  return formatted || String(value);
}

function formatSignedBillAmount(amount, currency = "USD") {
  // Render API amount as-is — never flip signs on the frontend.
  if (typeof amount === "string" && /[+\-$]/.test(amount)) return amount;
  const num = Number(amount);
  if (!Number.isFinite(num)) return "—";

  const hasCents = Math.round(Math.abs(num) * 100) % 100 !== 0;
  const abs = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(Math.abs(num));

  if (num >= 0) return `+${abs}`;
  return `-${abs}`;
}

function normalizeBillStatus(status) {
  const raw = String(status || "").toLowerCase();
  if (raw === "paid") return "Paid";
  if (raw === "pending") return "Pending";
  if (raw === "failed") return "Failed";
  if (raw === "cancelled" || raw === "canceled") return "Cancelled";
  return status ? String(status) : "Pending";
}

function normalizeBillReason(reason) {
  return String(reason || "")
    .replace(/^Commission Pending\b/i, "Commission Payout")
    .trim();
}

/** Split "Commission Payout (Feb 1 - Feb 28)" into title + period for UI. */
function splitBillReason(reason) {
  const raw = normalizeBillReason(reason);
  const match = raw.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
  if (!match) {
    return { title: raw || "—", period: null };
  }
  const inner = String(match[2] || "").trim();
  const isPeriod =
    /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\b/i.test(inner) ||
    /\d{4}-\d{2}-\d{2}/.test(inner) ||
    /\s-\s/.test(inner);
  if (!isPeriod) {
    return { title: raw || "—", period: null };
  }
  return {
    title: String(match[1] || "").trim() || "Commission Payout",
    period: inner,
  };
}

function formatPeriodRangeLabel(periodFrom, periodTo) {
  if (!periodFrom || !periodTo) return null;
  const from = new Date(periodFrom);
  const to = new Date(periodTo);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  const fmt = (date) =>
    new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    }).format(date);
  return `${fmt(from)} - ${fmt(to)}`;
}

function mapBillItem(item, index = 0) {
  if (!item || typeof item !== "object") return null;
  const rawAmount = item.amount ?? item.signedAmount ?? 0;
  const type = item.type ? String(item.type) : null;
  // API owns signs: order_revenue +, commission_payout −
  const amount = Number(rawAmount) || 0;
  const rawOrderNumber = String(item.orderNumber ?? item.BackerID ?? "").trim();
  const orderNumber = rawOrderNumber
    ? rawOrderNumber.startsWith("#")
      ? rawOrderNumber
      : `#${rawOrderNumber}`
    : null;
  const billNumber = item.billNumber ?? item.id ?? `#${index + 1}`;
  const rawReason = item.reason ?? item.billReason ?? item.description ?? "—";
  const periodFromApi = formatPeriodRangeLabel(
    item.periodFrom,
    item.periodTo
  );
  const split = splitBillReason(rawReason);
  const period = periodFromApi || split.period;
  const title =
    String(type || "").toLowerCase() === "commission_payout"
      ? "Commission Payout"
      : split.title;
  const reason = period ? `${title} (${period})` : title;
  return {
    id: item.id ?? String(billNumber),
    number: orderNumber,
    billNumber: String(billNumber).startsWith("#")
      ? String(billNumber)
      : `#${billNumber}`,
    orderNumber,
    date: formatBillDate(item.date ?? item.createdAt ?? item.billDate),
    reason,
    reasonTitle: title,
    reasonPeriod: period,
    billReason: reason,
    amount: formatSignedBillAmount(amount, item.currency || "USD"),
    amountRaw: Number(amount) || 0,
    status: normalizeBillStatus(item.status),
    type,
  };
}

/** Normalize GET /va/bills response. */
export function normalizeVaBillsResponse(response) {
  const payload =
    response?.data && !Array.isArray(response.data)
      ? response.data
      : response;

  const itemsRaw =
    payload?.items ??
    payload?.bills ??
    (Array.isArray(payload?.data) ? payload.data : null) ??
    (Array.isArray(response?.items) ? response.items : null) ??
    (Array.isArray(response) ? response : []);

  const items = (Array.isArray(itemsRaw) ? itemsRaw : [])
    .map(mapBillItem)
    .filter(Boolean);

  const pagination =
    payload?.pagination ??
    response?.pagination ?? {
      page: payload?.page ?? 1,
      limit: payload?.limit ?? 10,
      total: payload?.total ?? items.length,
      totalPages:
        payload?.totalPages ??
        Math.max(1, Math.ceil((payload?.total ?? items.length) / 10)),
    };

  const page = Number(pagination.page) || 1;
  const limit = Number(pagination.limit) || 10;
  const total =
    pagination.total != null && pagination.total !== ""
      ? Number(pagination.total)
      : items.length;
  const totalPages = Math.max(
    1,
    Number(pagination.totalPages) || Math.ceil(total / limit) || 1
  );

  return {
    success: true,
    data: {
      items,
      pagination: {
        page,
        limit,
        total,
        totalPages,
        hasNextPage:
          pagination.hasNextPage != null
            ? Boolean(pagination.hasNextPage)
            : page < totalPages,
        hasPrevPage:
          pagination.hasPrevPage != null
            ? Boolean(pagination.hasPrevPage)
            : page > 1,
      },
    },
  };
}

